import classNames from 'classnames';
import React from 'react';
import { DisplayClassification } from 'src/components/Exchange/types';
import If from 'src/components/Shared/If/If';
import ToolTip from 'src/components/Shared/ToolTip/ToolTip';
import { AGREEMENT_TYPES, ProductTypes } from 'src/settings/settings';
import { isAPP } from 'src/utils/advice/advice-utility';
import {
  isAutoExchange,
  isCollegeExchange,
  isExchange,
  isAutomaticSell,
  isRothConversion,
  isSell,
  isQCD,
} from 'src/utils/transactions-util';
import { formatShares } from 'src/utils/utils';
import words from 'src/utils/words';
import config from 'src/config/config';
import styles from './ExpandedFundShares.module.scss';

export interface ExpandedFundSharesProps {
  agreementType: AGREEMENT_TYPES;
  availableShares: number;
  coveredShares: number;
  displayClassification?: DisplayClassification;
  productType: ProductTypes;
  shares: number;
  uncoveredShares: number;
}

export const ExpandedFundShares: React.FC<ExpandedFundSharesProps> = ({
  agreementType,
  availableShares,
  coveredShares,
  displayClassification,
  productType,
  shares,
  uncoveredShares,
}) => {
  const localShares = isAPP(productType) ? undefined : shares;
  const effectiveShares =
    isExchange() ||
    isCollegeExchange() ||
    isAutoExchange() ||
    isAutomaticSell() ||
    isRothConversion()
      ? localShares
      : availableShares;

  const sharesLabel = formatShares(effectiveShares ?? 0, words.share);

  const shouldShowSharesLabel =
    (displayClassification === DisplayClassification.RETIREMENT ||
      displayClassification === DisplayClassification.COLLEGE_SAVINGS) &&
    agreementType !== AGREEMENT_TYPES.NON_FIDUCIARY_RETIREMENT_PLAN;

  const shouldShowToolTip =
    displayClassification === DisplayClassification.NON_RETIREMENT;

  const isAutomatic = () => {
    return isAutoExchange() || isAutomaticSell();
  };
  return (
    <div
      className={`exclude ${classNames(
        'e2e-expanded-fund-shares',
        styles['expanded-fund-shares']
      )}`}
    >
      <If test={shouldShowSharesLabel}>{sharesLabel}</If>
      <If test={shouldShowToolTip}>
        <ToolTip
          callerText={
            <span className={styles['tool-tip-text']}>{sharesLabel}</span>
          }
          message={
            <p className={`${styles['exclude']} non-covered-shares`}>
              {isSell() ||
              isQCD() ||
              isExchange() ||
              isAutoExchange() ||
              isAutomaticSell() ? (
                <>
                  This fund contains{' '}
                  <span className={styles['bold-text']}>
                    {formatShares(uncoveredShares)} Noncovered Shares
                  </span>{' '}
                  and{' '}
                  <span className={styles['bold-text']}>
                    {formatShares(coveredShares)} Covered Shares
                  </span>
                  . Noncovered shares (shares purchased before 2012) will be
                  redeemed before covered shares regardless of the cost basis{' '}
                  {isAutomatic() && <>method selected.</>}
                  {!isAutomatic() && (
                    <>
                      method selected, unless you{' '}
                      <a
                        href={`${config.url}/personal-investing/help/contact/index.html`}
                        rel="noreferrer"
                        target="_blank"
                      >
                        contact us
                      </a>{' '}
                      to request a redemption using the Specific Lot
                      Identification (SLID) method.
                    </>
                  )}{' '}
                  Once all noncovered shares are redeemed, covered shares will
                  be redeemed using the cost basis method selected for the
                  account.
                </>
              ) : (
                <>
                  {' '}
                  This fund contains{' '}
                  <span
                    className={`exclude ${styles['expanded-fund-uncovered-shares']}`}
                  >
                    {formatShares(uncoveredShares, 'Noncovered', words.Share)}
                  </span>{' '}
                  and{' '}
                  <span
                    className={`exclude ${styles['expanded-fund-uncovered-shares']}`}
                  >
                    {formatShares(coveredShares, 'Covered', words.Share)}.
                  </span>{' '}
                  Noncovered {words.shares} ({words.shares} purchased before
                  2012) do not have an assigned cost basis. Non-covered{' '}
                  {words.shares} will be {words.redeemed} before covered{' '}
                  {words.shares} unless you request a {words.redemption} using
                  the Specific Lot Identification (SLID) method.
                </>
              )}
            </p>
          }
        />
      </If>
    </div>
  );
};
